import { raiseErrorToast } from '@celito.clients/utils';
import { PopoverProps } from '@fluentui/react-components';
import { useEffect, useState } from 'react';

import {
  getRecordActivityLogs,
  getWorkflowHistory,
} from '../workflow-history/workflow-actions/services';
import { WizardStateComponentProps } from './wizard-state.model';
import WizardStateView from './wizard-state.view';

const mapStagesToGroups = (
  stageLabels: string[],
  lifecycleStageGroups?: Record<
    string,
    { stages: string[]; displayActively: boolean; displayOrder: number }
  >
): string[] => {
  if (!lifecycleStageGroups) return [];
  const groupNames: string[] = [];
  for (const stageLabel of stageLabels) {
    for (const [groupLabel, group] of Object.entries(lifecycleStageGroups)) {
      if (
        group.stages.includes(stageLabel) &&
        !groupNames.includes(groupLabel)
      ) {
        groupNames.push(groupLabel);
      }
    }
  }
  return groupNames;
};

export const WizardStateController = (props: WizardStateComponentProps) => {
  const [open, setOpen] = useState(false);
  const [taskGroupNames, setTaskGroupNames] = useState<string[]>([]);
  const [stagesLoading, setStagesLoading] = useState(true);
  const handleOpenChange: PopoverProps['onOpenChange'] = (e, data) => {
    // check if the id's dropdown options and no result options are in view port then keep open the workflow history
    // else let event handle the selection
    const target = e.currentTarget as EventTarget & (HTMLElement | Document);

    if (target instanceof HTMLElement || target instanceof Document) {
      const dropdownWithOptions = target.querySelector(
        '#dropdown-with-options'
      );
      const dropdownWithoutOptions = target.querySelector(
        '#dropdown-with-no-results'
      );
      if (dropdownWithOptions || dropdownWithoutOptions) {
        setOpen(true);
      } else {
        setOpen(data.open || false);
      }
    } else {
      setOpen(data.open || false);
    }
  };
  useEffect(() => {
    getTasks();
  }, [props?.recordName, props?.objectName, props?.recordData?.version]);

  const getTasks = async () => {
    setStagesLoading(true);
    const groupNames: string[] = [];
    try {
      if (props?.objectName && props?.recordName) {
        // Fetch workflow task history and activity logs in parallel
        const [taskList, activityLogs] = await Promise.all([
          getWorkflowHistory(
            props.objectName,
            props.recordName,
            props?.objectDefinitions?.isVersioningEnabled
              ? (props?.recordData?.version as string)
              : ''
          ),
          // Fetch activity logs as supplementary source for stage traversal
          // This covers lifecycle transitions driven by parent records (e.g., Root Cause, Investigation)
          // where no workflow tasks are created
          getRecordActivityLogs(props.objectName, props.recordName),
        ]);
        if (taskList?.tasks?.length) {
          taskList?.tasks?.forEach((task) => {
            groupNames.push(task?.objectRecordLifecycleGroup);
          });
        }
        if (activityLogs?.length) {
          // Filter activity logs to only include entries belonging to the parent record.
          // The API returns logs for both parent and child records (e.g., Deviation + CAPA).
          // Without this filter, child stages with the same name as parent stages
          // (e.g., both have "In Implementation") would incorrectly highlight on the parent chevron.
          // Note: Activity log "recordName" contains the record LABEL (e.g., "IN-000009"),
          // not the system name (e.g., "in00000000013__c"), so compare against recordData.label.
          const recordLabel = props.recordData?.label as string | undefined;
          const parentLogs = recordLabel
            ? activityLogs.filter((log) => {
                const logRecordName =
                  log['record_name__s'] ?? log['recordName'];
                return typeof logRecordName === 'string'
                  ? logRecordName === recordLabel
                  : true;
              })
            : activityLogs;

          const stageLabels = parentLogs
            .map(
              (log) =>
                (log['record_status__s'] ?? log['recordStatus']) as string
            )
            .filter(Boolean);

          // Filter stage labels to only include stages defined in the parent object's lifecycle.
          // This prevents child record stages (e.g., CAPA's "In Implementation") from being
          // mapped to parent object chevron groups (e.g., Deviation's "Implementation").
          const validStages = new Set<string>();
          if (props?.objectDefinitions?.lifecycleStageGroups) {
            for (const group of Object.values(
              props.objectDefinitions.lifecycleStageGroups
            )) {
              for (const stage of group.stages) {
                validStages.add(stage);
              }
            }
          }
          const filteredStageLabels =
            validStages.size > 0
              ? stageLabels.filter((label) => validStages.has(label))
              : stageLabels;

          const activityGroupNames = mapStagesToGroups(
            filteredStageLabels,
            props?.objectDefinitions?.lifecycleStageGroups
          );
          for (const name of activityGroupNames) {
            if (!groupNames.includes(name)) {
              groupNames.push(name);
            }
          }
        }

        if (groupNames.length) {
          setTaskGroupNames(groupNames);
        }
      }
    } catch (error) {
      raiseErrorToast(error);
    } finally {
      setStagesLoading(false);
    }
  };

  return (
    <WizardStateView
      {...props}
      loading={stagesLoading}
      handleOpenChange={handleOpenChange}
      open={open}
      taskGroupNames={taskGroupNames}
    />
  );
};
